import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import cx from "classnames";

import { platform } from "~/lib/utils";

import styles from "./TitleBarProvider.module.css";

/** This flag can be removed when desktop apps are all on v1.7.0 or above. */
export const oldTitleBarVisible = platform !== "browser" && !window.electron?.titleBarIsRemoved;

type ContextValue = {
	titleBarRef: HTMLDivElement | null;
	containerClassName?: string;
	setContainerClassName: (newName?: string) => void;
};
const TitleBarContext = createContext<ContextValue>({
	titleBarRef: null,
	setContainerClassName: () => console.warn("Attempting to set title bar class name before initialization?"),
});
TitleBarContext.displayName = "TitleBarContext";

export default function TitleBarProvider({ children }: React.PropsWithChildren<{}>) {
	// Using a callback ref instead of a real useRef because I _want_ things that use it to rerender if it changes.
	// Context: I noticed that during hot reloads, the title bar content would disappear. I think it's because the context
	// would not update if the element the ref is pointing to changed. I'm not sure if there's a real-world case where
	// this could happen, so I'm taking this approach to be safe. -Wal
	const [titleBar, setTitleBarRef] = useState<HTMLDivElement | null>(null);
	const titleBarRef = useCallback((node: HTMLDivElement | null) => setTitleBarRef(node), []);

	// TODO: instead of this we could probably have a "companion mode title bar" and a "regular title bar", since this is
	// only used by `CompanionModeTitleBar` to make it slimmer and adjust padding on windows.
	const [containerClassName, setContainerClassName] = useState<string>();

	const contextValue = useMemo<ContextValue>(
		() => ({ titleBarRef: titleBar, containerClassName, setContainerClassName }),
		[titleBar, containerClassName],
	);

	return (
		<TitleBarContext.Provider value={contextValue}>
			<div
				ref={titleBarRef}
				className={cx(styles.titleBarContainer, styles[platform], containerClassName, {
					[styles.hideDragArea]: platform === "browser" || oldTitleBarVisible,
				})}
			/>
			{children}
		</TitleBarContext.Provider>
	);
}

export function TitleBarPortal({
	children,
	instanceName,
	containerClassName,
}: React.PropsWithChildren<{ instanceName: string; containerClassName?: string }>) {
	const { titleBarRef, setContainerClassName } = useContext(TitleBarContext);

	setContainerClassName(containerClassName);

	if (!titleBarRef) return null;

	return createPortal(children, titleBarRef, instanceName);
}
